import React from 'react';
import {Box, Card, Group, Stack, Text} from '@mantine/core';
// Imported, not a `/assets/…` public path: the design-system bundle inlines
// it, where a root-relative URL resolves against the wrong origin (#325).
import nodusLogo from "@/assets/nodus_logo-128.png";

// Skeleton for the main app layout during initial loading
export const AppSkeleton: React.FC = () => {
  return (
    <Box style={{minHeight:'100vh',backgroundColor:'var(--mantine-color-body)'}}>
      {/* Header skeleton */}
      <Box component="header" style={{backgroundColor:'var(--mantine-color-body)',backdropFilter:'blur(8px)',borderBottom:'1px solid var(--mantine-color-default-border)'}}>
        <Box style={{maxWidth:1280,marginLeft:'auto',marginRight:'auto',paddingLeft:16,paddingRight:16,display:'flex',height:56,alignItems:'center'}}>
          {/* Logo skeleton */}
          <Group gap="sm">
            <Box style={{width:32,height:32,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
            <Box style={{height:24,width:96,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
          </Group>
          
          {/* Navigation skeleton */}
          <Box component="nav" style={{display:'flex',alignItems:'center',gap:24,marginLeft:24}}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Box key={i} style={{height:16,width:64,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
            ))}
          </Box>
          
          {/* Right side actions skeleton */}
          <Box style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:16}}>
            <Box style={{width:32,height:32,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
            <Box style={{width:32,height:32,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
          </Box>
        </Box>
      </Box>

      {/* Main content skeleton */}
      <Box style={{display:'flex'}}>
        {/* Sidebar skeleton */}
        <Box component="aside" style={{backgroundColor:'var(--mantine-color-body)',backdropFilter:'blur(8px)',width:256,borderRight:'1px solid var(--mantine-color-default-border)'}}>
          <Box style={{padding:16}}>
            <Box style={{height:16,width:128,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite',marginBottom:16}} />
            <Stack gap="sm">
              {Array.from({ length: 6 }).map((_, i) => (
                <Box key={i} style={{height:40,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
              ))}
            </Stack>
          </Box>
        </Box>

        {/* Main content area skeleton */}
        <Box component="main" flex={1}>
          <Box style={{maxWidth:1536,marginLeft:'auto',marginRight:'auto',paddingLeft:16,paddingRight:16,paddingBottom:32}}>
            {/* Page title skeleton */}
            <Box style={{marginBottom:24}}>
              <Box style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
                <Box style={{width:20,height:20,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                <Box style={{height:32,width:128,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
              </Box>
            </Box>

            {/* Dashboard stats skeleton */}
            <Box  style={{display:'grid',gap:24,marginBottom:32}}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} style={{height:'100%'}}>
                  <Card.Section style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'space-between',paddingBottom:8,gap:0}}>
                    <Box style={{height:16,width:96,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                    <Box style={{width:24,height:24,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                  </Card.Section>
                  <Card.Section>
                    <Box style={{height:32,width:64,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                  </Card.Section>
                </Card>
              ))}
            </Box>

            {/* Room occupancy and network stats skeleton */}
            <Box  style={{display:'grid',gap:24}}>
              {/* Room occupancy skeleton */}
              <Card style={{height:'100%'}}>
                <Card.Section>
                  <Group gap="sm">
                    <Box style={{width:24,height:24,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                    <Box style={{height:20,width:128,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                  </Group>
                  <Box style={{height:16,width:192,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                </Card.Section>
                <Card.Section>
                  <Stack gap="md">
                    <Box style={{display:'grid',gridTemplateColumns:'repeat(2, minmax(0, 1fr))',gap:16}}>
                      <Box style={{display:'flex',alignItems:'center',justifyContent:'space-between',border:'1px solid var(--mantine-color-default-border)',borderRadius:'var(--mantine-radius-sm)',padding:12}}>
                        <Box style={{height:16,width:96,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                        <Box style={{height:24,width:32,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                      </Box>
                      <Box style={{display:'flex',alignItems:'center',justifyContent:'space-between',border:'1px solid var(--mantine-color-default-border)',borderRadius:'var(--mantine-radius-sm)',padding:12}}>
                        <Box style={{height:16,width:80,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                        <Box style={{height:24,width:32,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                      </Box>
                    </Box>
                    <Box style={{height:'200px',width:'100%',backgroundColor:'color-mix(in srgb, var(--mantine-color-default-hover) 20%, transparent)',borderRadius:'var(--mantine-radius-md)',animation:'pulse 2s ease-in-out infinite'}}>
                      <Box style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}>
                        <Box style={{display:'flex',flexDirection:'column',alignItems:'center',gap:16}}>
                          <Box style={{width:128,height:128,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:9999,animation:'pulse 2s ease-in-out infinite'}} />
                          <Box style={{height:16,width:128,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                        </Box>
                      </Box>
                    </Box>
                  </Stack>
                </Card.Section>
              </Card>

              {/* Network stats skeleton */}
              <Card style={{height:'100%'}}>
                <Card.Section>
                  <Group gap="sm">
                    <Box style={{width:24,height:24,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                    <Box style={{height:20,width:128,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                  </Group>
                  <Box style={{height:16,width:192,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                </Card.Section>
                <Card.Section>
                  <Box style={{height:'300px',width:'100%',backgroundColor:'color-mix(in srgb, var(--mantine-color-default-hover) 20%, transparent)',borderRadius:'var(--mantine-radius-md)',animation:'pulse 2s ease-in-out infinite'}}>
                    <Box style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}>
                      <Box style={{display:'flex',flexDirection:'column',alignItems:'center',gap:16}}>
                        <Box style={{width:96,height:96,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                        <Box style={{height:16,width:128,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                      </Box>
                    </Box>
                  </Box>
                </Card.Section>
              </Card>
            </Box>

            {/* Recent activity and quick search skeleton */}
            <Box  style={{display:'grid',gap:24,marginTop:24}}>
              <Card>
                <Card.Section>
                  <Box style={{height:20,width:128,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                  <Box style={{height:16,width:192,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                </Card.Section>
                <Card.Section>
                  <Stack gap="md">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Box key={i} style={{borderLeft:'4px solid var(--mantine-color-default-border)',borderColor:'var(--mantine-primary-color-filled)',paddingLeft:16,paddingTop:8,paddingBottom:8}}>
                        <Box style={{height:16,width:128,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite',marginBottom:8}} />
                        <Box style={{height:12,width:192,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite',marginBottom:4}} />
                        <Box style={{height:12,width:96,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                      </Box>
                    ))}
                  </Stack>
                </Card.Section>
              </Card>

              <Card>
                <Card.Section>
                  <Box style={{height:20,width:96,backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                </Card.Section>
                <Card.Section>
                  <Box style={{height:40,width:'100%',backgroundColor:'var(--mantine-color-default-hover)',borderRadius:'var(--mantine-radius-sm)',animation:'pulse 2s ease-in-out infinite'}} />
                </Card.Section>
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Loading screen shown while app chunks load (e.g. after login)
export const MinimalAppSkeleton: React.FC = () => {
  return (
    <Box
      style={{minHeight:'100vh',backgroundColor:'var(--mantine-color-body)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:32,paddingLeft:16,paddingRight:16}}
      role="status"
      aria-label="Loading application"
    >
      {/* Logo */}
      <Group gap="md">
        <img
          src={nodusLogo}
          alt=""
          style={{opacity:0.9,height:48,width:48,objectFit:'contain'}}
          width={48}
          height={48}
        />
        <Text component="span" fz="xl" fw={600} style={{letterSpacing:'-0.025em'}}>
          Nodus
        </Text>
      </Group>

      {/* Spinner + message */}
      <Box style={{display:'flex',flexDirection:'column',alignItems:'center',gap:16}}>
        <Box
          // borderTopColor must follow the borderColor shorthand, which would
          // otherwise reset it and hide the moving segment.
          style={{height:40,width:40,borderRadius:9999,borderWidth:2,borderStyle:'solid',borderColor:'color-mix(in srgb, var(--mantine-primary-color-filled) 30%, transparent)',borderTopColor:'var(--mantine-primary-color-filled)',animation:'spin 1s linear infinite'}}
          aria-hidden
        />
        <Text component="p" fz="sm" c="dimmed" style={{animation:'pulse 2s ease-in-out infinite'}}>
          Preparing your workspace…
        </Text>
      </Box>

    </Box>
  );
}; 